import { ShowDialogProps } from '../../show-dialog'
import { SendToLocalityDialogForm } from './SendToLocalityDialogForm'
import { useGetMainComponentById } from '../../api/main-component'
import { MovementType, IMainComponentType as Type } from '../../enums'
import { useEffect, useState } from 'react'
import { IMainComponentType } from 'src/generated-types/main-component-type'
import { CenteredSpinner } from 'src/components/Spinner'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { UseFormReturn } from 'react-hook-form'
import { useGetFormSchemasForMovement } from 'src/api/form-schema'
import { useCompleteChecklistsDialog } from 'src/hooks/useCompleteChecklistsDialog'

interface SendToLocalityDialogProps extends ShowDialogProps {
  customerId: EntityId
  mainComponentId: EntityId
  mainComponentTypeId?: EntityId
  onSubmit: (form: any, pop: () => void, formContext?: UseFormReturn<any>) => void
}

export const SendToLocalityDialog: React.FC<SendToLocalityDialogProps> = ({
  pop,
  customerId,
  mainComponentId,
  mainComponentTypeId,
  onSubmit,
}) => {
  const { t } = useTranslation(['certificate', 'main-component'])
  const showChecklistsDialog = useCompleteChecklistsDialog(customerId as EntityId)
  const { data: mainComponent, isLoading } = useGetMainComponentById(customerId, mainComponentId)
  const isCertificateAddFormVisible =
    mainComponent?.isValidCertificateAvailable != null ? !mainComponent?.isValidCertificateAvailable : true

  const { data: sendToLocalitySchemas, isLoading: isLoadingSendToLocalitySchemas } = useGetFormSchemasForMovement(
    customerId as EntityId,
    { equipmentTypeIds: [mainComponentTypeId as number], movementTypes: [MovementType.SendToLocality] },
  )
  const isCertificateRequired = mainComponent?.type?.componentType === Type.Net ? true : false
  const isPlacementVisible =
    mainComponent?.type.componentType == Type.Net ||
    mainComponent?.type.componentType == Type.Ring ||
    mainComponent?.type.componentType == Type.LiceSkirt

  const [mainComponentTypeDetails, setMainComponentTypeDetails] = useState<{
    mainComponentType: IMainComponentType | undefined
    isCertificateDetailsRequired: boolean
  }>({
    mainComponentType: mainComponent?.type,
    isCertificateDetailsRequired: mainComponent?.type?.componentType === Type.Net,
  })
  useEffect(() => {
    if (mainComponent && mainComponent.type?.componentType != Type.Net) return
    if (
      mainComponent &&
      mainComponent.isCertificationRuleValid &&
      !mainComponent.isValidCertificateAvailable &&
      (mainComponent.certificates?.length ?? 0) > 0
    ) {
      if (mainComponent.isInvalidWithOneYearHold && mainComponent.isCertificationRuleValid) {
        toast.warn(t('invalidDueToYearHold', { ns: 'certificate' }))
        return
      }
    }
  }, [mainComponent])

  useEffect(() => {
    setMainComponentTypeDetails({
      mainComponentType: mainComponent?.type,
      isCertificateDetailsRequired: isCertificateRequired && isCertificateAddFormVisible,
    })
  }, [mainComponent])

  if (isLoading) return <CenteredSpinner containerProps={{ height: 600, width: 400 }} />

  return (
    <SendToLocalityDialogForm
      onSubmit={async (formData, formContext) => {
        formData.mainComponentTypeId = mainComponent?.typeId
        formData.mainComponentId = mainComponent?.id

        const {
          certificateNumber,
          certificateDate,
          validUntil,
          certificateDocumentType,
          certificateFileId,
          certificateSupplierId,
          validPeriod,
          ...rest
        } = formData
        if (mainComponentTypeDetails?.isCertificateDetailsRequired) {
          rest.certificate = {
            certificateNumber,
            certificateDate,
            validUntil,
            certificateDocumentType,
            validPeriod,
            fileId: certificateFileId,
            supplierId: certificateSupplierId,
            mainComponentId: mainComponent?.id,
          }
        }

        await showChecklistsDialog({
          schemas: sendToLocalitySchemas,
          isLoadingSchemas: isLoadingSendToLocalitySchemas,
          confirmText: t('sendToLocality', { ns: 'main-component' }),
          maxCountValidation: { positionId: rest.selectedPositionId, equipmentIds: [mainComponentId as EntityId] },
          mainComponentId: mainComponentId as EntityId,
          onConfirm: reports => {
            rest.formSchemaReports = reports
            onSubmit(rest, pop, formContext)
          },
        })
      }}
      onCancel={pop}
      customerId={mainComponent?.customerId as EntityId}
      isCertificateAddFormVisible={isCertificateAddFormVisible}
      isPlacementVisible={isPlacementVisible}
      mainComponentTypeDetails={mainComponentTypeDetails}
      setMainComponentTypeDetails={
        mainComponent?.type?.componentType === Type.Net ? undefined : setMainComponentTypeDetails
      }
      mainComponent={mainComponent}
    />
  )
}
